import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Plus, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import InputCst from "@components/InputCst/InputCst";
import { UogItem } from "../types";

interface UogSectionProps {
  value: UogItem[];
  onChange: (next: UogItem[]) => void;
}

const UogSection: React.FC<UogSectionProps> = ({ value, onChange }) => {
  const { t } = useTranslation("user-wizard-v2");
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [uog, setUog] = useState("");
  const [description, setDescription] = useState("");

  const closeModal = () => {
    setOpen(false);
    setUog("");
    setDescription("");
  };

  const handleAdd = () => {
    if (!uog.trim()) return;
    onChange([
      ...value,
      { uog: uog.trim(), uog_description: description.trim() },
    ]);
    closeModal();
  };

  const handleDelete = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {t("uog.title")}
          </Typography>
          <Typography
            sx={{
              fontSize: "13px",
              color: theme.palette.text.primary,
              mt: 0.5,
            }}
          >
            {t("uog.note")}
          </Typography>
        </Box>
        <Button
          startIcon={<Plus size={16} />}
          onClick={() => setOpen(true)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: theme.palette.primary.main,
            flexShrink: 0,
          }}
        >
          {t("uog.add")}
        </Button>
      </Box>

      {value.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "8px",
            p: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
              {item.uog}
            </Typography>
            {item.uog_description && (
              <Typography
                sx={{ fontSize: "13px", color: theme.palette.text.primary }}
              >
                {item.uog_description}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={() => handleDelete(index)}
            sx={{ color: theme.palette.primary.main }}
            aria-label={t("uog.delete") || "delete"}
          >
            <Trash2 size={18} />
          </IconButton>
        </Box>
      ))}

      <Dialog
        open={open}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "12px",
            p: 3,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: "22px", fontWeight: 700 }}>
            {t("uog.modalTitle")}
          </Typography>
          <IconButton
            onClick={closeModal}
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <InputCst
              id="uog-name"
              label={t("uog.fieldName")}
              value={uog}
              onChange={(e: any) => setUog(e.target.value)}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <InputCst
              id="uog-description"
              label={t("uog.fieldDescription")}
              value={description}
              onChange={(e: any) => setDescription(e.target.value)}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!uog.trim()}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
          >
            {t("uog.add")}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default UogSection;
